import { ApolloError } from '@apollo/client';
import { currentPresetBrand } from './presetBrand';
import i18n from '../i18n';
import { localizeConstraint, localizeServerError } from '../i18n/errors';

export interface Response {
  data: {
    message: {
      constraints: any;
    }[]
  };
}
export const extractMessages = (response: Response) => {
  const { message } = response.data;
  let errorString = '';

  message.forEach((error: any) => {
    if (error.constraints) {
      errorString += Object.values(error.constraints).join('. ');
    } else if (typeof error === 'string') {
      errorString += error;
    }
  });

  return errorString;
};

/**
 * What a Nest HttpException put in a GraphQL error: `{ statusCode, error,
 * message }`, or undefined. Not every error carries it - a library's own
 * exception (the reCAPTCHA module's), a network failure or a server crash
 * comes back without `extensions.response`, and reading `.message` off it
 * used to throw inside the error handler ("Cannot destructure property
 * 'message' ...", QA 2026-09-25 #3), so the visitor saw nothing at all.
 */
export interface GraphqlErrorResponse {
  statusCode?: number;
  error?: string;
  message?: unknown;
  [key: string]: unknown;
}

export const graphqlErrorResponse = (
  error?: unknown,
): GraphqlErrorResponse | undefined => {
  const first = (error as ApolloError | undefined)?.graphQLErrors?.[0];
  const response = first?.extensions?.response;
  return response && typeof response === 'object'
    ? response as GraphqlErrorResponse
    : undefined;
};

export const extractGraphqlErrors = (error?: ApolloError): string => {
  const message = graphqlErrorResponse(error)?.message;
  return typeof message === 'string' ? message : '';
};

/**
 * Booking refusals whose server message is written for the customer and
 * says what to do (server: AppointmentService assertBookable,
 * BookingAssistanceService).
 */
export const CUSTOMER_FACING_ERRORS = [
  'EMPTY_ORDER',
  'BOOKING_DATE_IN_PAST',
  'BOOKING_DATE_TOO_FAR',
  'INVALID_PHONE',
  'RECAPTCHA_FAILED',
  'DISCOUNT_CODE_INVALID',
];

export const extractSpecificGraphqlErrors = (error?: ApolloError): string => {
  const first = error?.graphQLErrors?.[0];
  const response = (first?.extensions)?.response;

  if (response?.error === 'USER_ALREADY_EXISTS'
    && response?.message
  ) {
    return localizeServerError(response.error, response.message);
  }

  if (CUSTOMER_FACING_ERRORS.includes(response?.error)
    && typeof response?.message === 'string'
  ) {
    return localizeServerError(response.error, response.message);
  }

  if (response?.statusCode === 422 && Array.isArray(response?.message)) {
    interface ConstraintMessage {
      constraints?: { [key: string]: string };
    }

    const msgs: string[] = (response.message as ConstraintMessage[])
      .flatMap((m: ConstraintMessage) => Object.values(m?.constraints ?? {}))
      .map(localizeConstraint)
      .filter((m, i, all) => all.indexOf(m) === i);
    return msgs.join(' ') || i18n.t('errors.validationFailed');
  }

  // A preset tenant (Aperio) points at its own inbox, never Go Oil's.
  const brand = currentPresetBrand();
  const support = brand ? brand.email : 'support@gooil.ca';
  return i18n.t('errors.generic', { email: support });
};

export const extractStripePaymentError = (error?: ApolloError): string => {
  if (!error || !error.graphQLErrors?.[0]) {
    return 'An error occurred while saving your card';
  }

  const gqlError = error.graphQLErrors[0];
  const responseData = gqlError.extensions?.response;

  if (!responseData) {
    return gqlError.message || 'An error occurred while saving your card';
  }

  const declineCode = responseData.decline_code;
  const { message } = responseData;

  const errorMessages: { [key: string]: string } = {
    insufficient_funds:
    'Your card has insufficient funds. Please use a different card.',
    card_declined:
    'Your card was declined. Please contact your bank or use a different card.',
    generic_decline:
    'Your card was declined. Please contact your bank or use a different card.',

    expired_card: 'Your card has expired. Please use a different card.',
    incorrect_cvc:
    'The security code (CVC) is incorrect. Please check and try again.',
    incorrect_number:
    'The card number is incorrect. Please check and try again.',

    fraudulent:
    'This payment was flagged for security reasons. Please contact your bank.',
    stolen_card:
    'This card has been reported as stolen. Please contact your bank.',
    lost_card:
    'This card has been reported as lost. Please contact your bank.',

    processing_error:
    'An error occurred while processing your card. Please try again.',
    try_again_later:
    'Unable to process your payment. Please try again later.',

    card_not_supported:
    'This type of card is not supported. Please use a different card.',
    do_not_honor:
    'Your card was declined. Please contact your bank for more information.',
  };

  return errorMessages[declineCode]
  || message || 'Unable to add new payment method. Please try again.';
};
